import { useEffect, useState } from "react";
import { fetchCareerTree, fetchPromotionReadiness, fetchTargetRole, setTargetRole, clearTargetRole } from "../lib/api";
import { Loader2, MapPin, TrendingUp, ArrowRight, Award, Sparkles, Target } from "lucide-react";
import { toast } from "sonner";

const nodeColor = (n, isPinned) => {
  if (n.type === "current") return { bg: "#0f172a", border: "#0f172a", text: "#fff" };
  if (isPinned) return { bg: "#fef3c7", border: "#f59e0b", text: "#78350f" };
  if (n.type === "promotion") return { bg: "#ecfdf5", border: "#059669", text: "#065f46" };
  return { bg: "#eff6ff", border: "#0891b2", text: "#0c4a6e" }; // lateral
};

export default function CareerTreeView({ employeeId, onTargetChange }) {
  const [tree, setTree] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [pinnedTargetId, setPinnedTargetId] = useState(null);

  useEffect(() => {
    if (!employeeId) return;
    setLoading(true);
    Promise.all([fetchCareerTree(employeeId), fetchTargetRole(employeeId)])
      .then(([t, tgt]) => { setTree(t); setPinnedTargetId(tgt?.target_role_id || null); })
      .catch(() => setTree(null))
      .finally(() => setLoading(false));
  }, [employeeId]);

  useEffect(() => {
    if (!selected || !employeeId) { setReadiness(null); return; }
    fetchPromotionReadiness(employeeId, selected.id).then(setReadiness).catch(() => setReadiness(null));
  }, [selected, employeeId]);

  const handlePinTarget = async (roleId, roleTitle) => {
    try {
      if (pinnedTargetId === roleId) {
        await clearTargetRole(employeeId);
        setPinnedTargetId(null);
        toast.success("Hedef rol kaldırıldı");
      } else {
        await setTargetRole(employeeId, { target_role_id: roleId, note: "" });
        setPinnedTargetId(roleId);
        toast.success(`🎯 Hedefin belirlendi: ${roleTitle}`);
      }
      onTargetChange?.();
    } catch {
      toast.error("Hedef güncellenemedi");
    }
  };

  if (loading) return <div className="card-surface p-6 flex items-center justify-center gap-3 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Kariyer ağacı yükleniyor...</div>;
  if (!tree) return <div className="card-surface p-6 text-slate-500">Ağaç oluşturulamadı.</div>;

  const lanes = tree.lanes || [];
  const nodes = tree.nodes || [];
  // Group nodes by band_level
  const nodesByLane = {};
  nodes.forEach((n) => {
    nodesByLane[n.band_level] = nodesByLane[n.band_level] || [];
    nodesByLane[n.band_level].push(n);
  });

  return (
    <div className="space-y-4" data-testid="career-tree-view">
      <div className="card-surface p-4 flex items-center gap-3">
        <MapPin className="w-4 h-4 text-indigo-600" />
        <div className="flex-1">
          <div className="label-eyebrow">Kariyer Ağacı</div>
          <div className="text-sm text-slate-600">Her band kendi şeridinde; siyah kart mevcut rolün, yeşil terfi, mavi yatay hareket.</div>
        </div>
        <div className="flex items-center gap-2 text-[10px] text-slate-500">
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-slate-900" /> Mevcut</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Terfi</span>
          <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-sky-500" /> Yatay</span>
        </div>
      </div>

      {/* Swim-lane tree */}
      <div className="card-surface p-4 overflow-x-auto">
        <div className="space-y-3 min-w-[720px]">
          {lanes.slice().reverse().map((lane) => {
            const laneNodes = nodesByLane[lane.level] || [];
            return (
              <div key={lane.level} className="flex items-stretch gap-3">
                <div className="w-24 flex-shrink-0 flex items-center justify-center text-xs font-medium text-slate-500 bg-slate-50 rounded-lg border border-slate-100 py-2">
                  {lane.label}
                </div>
                <div className="flex-1 flex flex-wrap gap-2 items-center min-h-[80px] p-2 rounded-lg bg-slate-50/40 border border-dashed border-slate-200">
                  {laneNodes.length === 0 ? (
                    <div className="text-xs text-slate-400 italic">Bu bantta rol yok</div>
                  ) : laneNodes.map((n) => {
                    const isPinned = pinnedTargetId === n.id;
                    const c = nodeColor(n, isPinned);
                    const isSelected = selected?.id === n.id;
                    return (
                      <div key={n.id} className="relative">
                        <button
                          type="button"
                          onClick={() => setSelected(n.type === "current" ? null : n)}
                          data-testid={`career-tree-node-${n.id}`}
                          className={`text-left rounded-lg px-3 py-2 border-2 transition-all hover:shadow-md hover:scale-[1.02] w-full ${isSelected ? "ring-2 ring-indigo-400" : ""} ${isPinned ? "ring-2 ring-amber-400" : ""}`}
                          style={{ background: c.bg, borderColor: c.border, color: c.text, minWidth: "170px" }}
                        >
                          <div className="text-xs opacity-70 flex items-center gap-1">
                            {n.type === "current" && <><Award className="w-3 h-3" /> Mevcut rolün</>}
                            {n.type === "promotion" && <><TrendingUp className="w-3 h-3" /> Terfi</>}
                            {n.type === "lateral" && <><ArrowRight className="w-3 h-3" /> Yatay hareket</>}
                            {isPinned && <span className="ml-auto inline-flex items-center gap-0.5 text-[10px] font-bold"><Target className="w-2.5 h-2.5" /> Hedef</span>}
                          </div>
                          <div className="text-sm font-semibold leading-tight mt-0.5">{n.title}</div>
                          <div className="text-[10px] mt-1 opacity-80">
                            {n.department}{n.type !== "current" && (<> · %{n.match_score} uygun</>)}
                          </div>
                          {n.type !== "current" && n.time_to_ready_months && (
                            <div className="text-[10px] mt-0.5 opacity-70">~{n.time_to_ready_months} ay hazırlık</div>
                          )}
                        </button>
                        {n.type !== "current" && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handlePinTarget(n.id, n.title); }}
                            className={`absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full text-[10px] font-bold shadow-sm border transition ${isPinned ? "bg-amber-500 text-white border-amber-600" : "bg-white text-slate-500 border-slate-300 hover:bg-amber-50 hover:text-amber-700"}`}
                            title={isPinned ? "Hedefi kaldır" : "Bunu hedef yap"}
                            data-testid={`career-tree-pin-${n.id}`}
                          >
                            <Target className="w-3 h-3 mx-auto" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected role prep panel */}
      {selected && (
        <div className="card-surface p-4 border-l-4 border-indigo-500" data-testid="career-tree-prep-panel">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="label-eyebrow flex items-center gap-1.5"><Sparkles className="w-3 h-3 text-indigo-600" /> Terfi Hazırlık Analizi</div>
              <h3 className="text-lg font-semibold text-slate-900">{selected.title}</h3>
              <div className="text-xs text-slate-500">{selected.band} · {selected.department}</div>
            </div>
            <button className="text-xs text-slate-500 hover:text-slate-700" onClick={() => setSelected(null)}>Kapat ✕</button>
          </div>
          {readiness ? (
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="text-3xl font-bold text-indigo-600">%{readiness.readiness_pct}</div>
                <div className="flex-1 text-sm text-slate-600">
                  {readiness.can_promote
                    ? <span className="text-emerald-700 font-medium">✓ Kriterlerin çoğunu karşılıyorsun — başvuru için hazır.</span>
                    : <span className="text-amber-700">Kriterlerin bir kısmı eksik — aksiyon planı aşağıda.</span>}
                  <div className="text-xs text-slate-500">{readiness.criteria_met}/{readiness.criteria_total} kriter · {readiness.mandatory_met}/{readiness.mandatory_total} zorunlu</div>
                </div>
              </div>
              <div className="space-y-1">
                {(readiness.checks || []).map((c) => (
                  <div key={c.id} className="flex items-center gap-2 text-xs p-2 rounded bg-white border border-slate-100">
                    <span className={`w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center text-white ${c.met ? "bg-emerald-500" : "bg-slate-300"}`}>{c.met ? "✓" : "○"}</span>
                    <div className="flex-1">{c.label}{c.mandatory && <span className="ml-1 text-[9px] text-red-600">*</span>}</div>
                    <div className="font-mono text-slate-700">
                      {typeof c.current === "number" ? c.current : c.current} <span className="text-slate-400">/ {c.target}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : <div className="text-xs text-slate-500">Yükleniyor...</div>}
        </div>
      )}
    </div>
  );
}
